import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, ArrowsMerge, Warning } from "@phosphor-icons/react";

const WorkerOption = ({ w }) => (
  <option value={w.id}>
    {w.name}{w.email ? ` — ${w.email}` : ""}{w.phone ? ` — ${w.phone}` : ""} ({w.status})
  </option>
);

export default function MergeWorkersModal({ workers, onClose, onDone }) {
  const [keepId, setKeepId] = useState("");
  const [dupId, setDupId] = useState("");
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setPreview(null); setErr("");
    if (!keepId || !dupId || keepId === dupId) return;
    api.post(`/workers/${keepId}/merge/${dupId}?dry_run=true`)
      .then((r) => setPreview(r.data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [keepId, dupId]);

  const doMerge = async () => {
    if (!window.confirm(`Permanently merge "${preview.duplicate.name}" into "${preview.keep.name}"?\n\nAll timesheets, assignments and pay history move to the kept record, and the duplicate is deleted. This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.post(`/workers/${keepId}/merge/${dupId}`);
      toast.success(`Merged into ${preview.keep.name}.`);
      onDone();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const sorted = [...workers].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="merge-workers-modal"
        className="bg-white rounded-lg w-full max-w-lg p-6 border border-slate-200 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-xl font-bold flex items-center gap-2"><ArrowsMerge size={22} /> Merge duplicate workers</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Everything on the duplicate (timesheets, assignments, pay run history, files, login)
          moves onto the record you keep. Blank profile fields on the kept record are filled
          from the duplicate. The duplicate is then deleted.
        </p>

        <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Record to KEEP</label>
        <select value={keepId} onChange={(e) => setKeepId(e.target.value)} data-testid="merge-keep-select"
          className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md text-sm bg-white">
          <option value="">— Select worker to keep —</option>
          {sorted.filter((w) => w.id !== dupId).map((w) => <WorkerOption key={w.id} w={w} />)}
        </select>

        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mt-4">Duplicate to merge in (will be deleted)</label>
        <select value={dupId} onChange={(e) => setDupId(e.target.value)} data-testid="merge-dup-select"
          className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md text-sm bg-white">
          <option value="">— Select duplicate —</option>
          {sorted.filter((w) => w.id !== keepId).map((w) => <WorkerOption key={w.id} w={w} />)}
        </select>

        {err && <div className="mt-3 text-sm text-red-600" data-testid="merge-error">{err}</div>}

        {preview && (
          <div className="mt-4 border border-slate-200 rounded-md p-4 bg-slate-50 text-sm" data-testid="merge-preview">
            <div className="tinylabel mb-2">What will happen</div>
            <ul className="space-y-1 text-slate-700">
              <li>• Move <b>{preview.moves.timesheets}</b> timesheet(s), <b>{preview.moves.assignments}</b> assignment(s), <b>{preview.moves.files}</b> file(s)</li>
              <li>• Update <b>{preview.moves.payruns_touched}</b> pay run(s) to point at "{preview.keep.name}"</li>
              <li>• Login: <b>{preview.login_action === "none" ? "no login on the duplicate" : preview.login_action}</b></li>
              {preview.fill_fields.length > 0 && (
                <li>• Fill blank fields on the kept record: <b>{preview.fill_fields.join(", ").replaceAll("_enc", "")}</b></li>
              )}
              <li>• Delete the duplicate record "{preview.duplicate.name}"</li>
            </ul>
            {preview.overlap_weeks.length > 0 && (
              <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800 text-xs" data-testid="merge-overlap-warning">
                <Warning size={16} className="shrink-0 mt-0.5" />
                <span>Both records have timesheets for {preview.overlap_weeks.length === 1 ? "the week of" : "these weeks:"} <b>{preview.overlap_weeks.join(", ")}</b> — after merging, review those weeks for double entries.</span>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="h-10 px-4 border border-slate-300 rounded-md text-sm font-semibold hover:bg-slate-50">Cancel</button>
          <button onClick={doMerge} disabled={!preview || busy} data-testid="merge-confirm-btn"
            className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md text-sm disabled:opacity-50">
            {busy ? "Merging…" : "Merge permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
